import { DatePicker, Form, Input } from 'antd';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import CalendarIcon from 'assets/icons/calendar';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FormSection from 'components/ui/form-section';
import { LazyLoadingSelect } from 'components/ui/lazy-loading-select';
import { SortedSelect } from 'components/ui/sorted-select';
import { EQueryKeys } from 'constants/query-keys';
import { FilterClaimsQueryDtoStatusesEnum } from 'libs/pinnacle-sdk';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
  catEventApi,
  countryStateCityApi,
  referenceDataApi,
  userApi,
} from 'services/apis';

const LossDetails: React.FC = () => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const [countryIso, setCountryIso] = useState<string | undefined>(undefined);

  const { data: countries, isLoading: isLoadingCountries } = useQuery(
    EQueryKeys.COUNTRIES,
    async () => countryStateCityApi.getCountries(),
    { refetchOnWindowFocus: false, staleTime: Infinity }
  );

  const { data: cities, isLoading: isLoadingCities } = useQuery(
    [EQueryKeys.CITIES, countryIso],
    async () => {
      if (!countryIso) return;
      return countryStateCityApi.getCitiesByStateAndCountry({ countryIso });
    },
    { refetchOnWindowFocus: false, staleTime: Infinity }
  );

  const { data: lossCauses, isLoading: isLoadingLossCauses } = useQuery(
    [EQueryKeys.LOSS_CAUSES],
    async () => referenceDataApi.getLossCauses(),
    { refetchOnWindowFocus: false, staleTime: Infinity }
  );

  useEffect(() => {
    const country = form.getFieldValue('country');
    if (countries && country) {
      const selectedCountry = countries.find((c) => c.name === country);
      if (selectedCountry) {
        setCountryIso(selectedCountry.iso);
      }
    }
  }, [countries, form]);

  const handleCountryChange = (value: string) => {
    form.setFieldValue('city', undefined);
    const selectedCountry = countries?.find((c) => c.name === value);
    setCountryIso(selectedCountry?.iso);
  };

  return (
    <FormSection
      title={t('claim.binderClaimsActions.lossDetails.title')}
      collapsible
    >
      {/* Row 1: Claims No, Claims Handler, Claims Status */}
      <FormLayoutGroup mdColumns={3}>
        <Form.Item
          name="claimsNo"
          label={t('claim.binderClaimsActions.lossDetails.claimsNo')}
          rules={[{ required: true }]}
        >
          <Input size="large" disabled />
        </Form.Item>

        <Form.Item
          name="claimsHandler"
          label={t('claim.binderClaimsActions.lossDetails.claimsHandler')}
          rules={[{ required: true }]}
        >
          <LazyLoadingSelect
            fetchOptions={async (page, pageSize, searchText) => {
              const { items } = await userApi.filterUsers({
                searchText,
                page,
                pageSize,
              });
              return {
                data: items.map((user) => ({
                  label: user.name || '',
                  value: user.userId,
                })),
              };
            }}
            suffixIcon={<ArrowDownIcon />}
            allowClear
            size="large"
            placeholder={t('claim.selectClaimsHandler')}
            queryKey="claims-handler-lazy-loading-select"
          />
        </Form.Item>

        <Form.Item
          name="claimsStatus"
          label={t('claim.binderClaimsActions.lossDetails.claimsStatus')}
          rules={[{ required: true }]}
        >
          <SortedSelect
            allowClear
            size="large"
            placeholder={t('claim.selectMasterClaimsStatus')}
            options={Object.values(FilterClaimsQueryDtoStatusesEnum).map(
              (status) => ({
                label: status,
                value: status,
              })
            )}
            suffixIcon={<ArrowDownIcon />}
          />
        </Form.Item>
      </FormLayoutGroup>

      {/* Row 2: Line of Business, Sub-Class */}
      <FormLayoutGroup mdColumns={2}>
        <Form.Item
          name="lineOfBusiness"
          label={t('claim.binderClaimsActions.lossDetails.lineOfBusiness')}
          rules={[{ required: true }]}
        >
          <Input size="large" disabled />
        </Form.Item>

        <Form.Item
          name="subClass"
          label={t('claim.binderClaimsActions.lossDetails.subClass')}
          rules={[{ required: true }]}
        >
          <Input size="large" disabled />
        </Form.Item>
      </FormLayoutGroup>

      {/* Row 3: Reinsured, Broker, Reference No */}
      <FormLayoutGroup mdColumns={3}>
        <Form.Item
          name="reinsured"
          label={t('claim.binderClaimsActions.lossDetails.reinsured')}
          rules={[{ required: true }]}
        >
          <Input size="large" disabled />
        </Form.Item>

        <Form.Item
          name="broker"
          label={t('claim.binderClaimsActions.lossDetails.broker')}
          rules={[{ required: true }]}
        >
          <Input size="large" disabled />
        </Form.Item>

        <Form.Item
          name="referenceNo"
          label={t('claim.binderClaimsActions.lossDetails.referenceNo')}
          rules={[{ required: true }]}
        >
          <Input
            size="large"
            placeholder={t('general.enter', {
              field: t('claim.binderClaimsActions.lossDetails.referenceNo'),
            })}
          />
        </Form.Item>
      </FormLayoutGroup>

      {/* Row 4: Reported Date, Date of Loss, Select Cat Code */}
      <FormLayoutGroup mdColumns={3}>
        <Form.Item
          name="reportedDate"
          label={t('claim.binderClaimsActions.lossDetails.reportedDate')}
          rules={[{ required: true }]}
        >
          <DatePicker
            format="DD/MM/YYYY"
            allowClear
            size="large"
            className="w-full"
            suffixIcon={<CalendarIcon />}
            placeholder={t('claim.selectDate')}
          />
        </Form.Item>

        <Form.Item
          name="dateOfLoss"
          label={t('claim.binderClaimsActions.lossDetails.dateOfLoss')}
          rules={[{ required: true }]}
        >
          <DatePicker
            format="DD/MM/YYYY"
            allowClear
            size="large"
            className="w-full"
            suffixIcon={<CalendarIcon />}
            placeholder={t('claim.selectDate')}
          />
        </Form.Item>

        <Form.Item
          name="catCode"
          label={t('claim.binderClaimsActions.lossDetails.selectCatCode')}
        >
          <LazyLoadingSelect
            fetchOptions={async (page, pageSize, searchText) => {
              const res = await catEventApi.filterCatEvents({
                searchText,
                page,
                pageSize,
              });
              return {
                data: (res.items || []).map((cat) => ({
                  label: cat.eventName,
                  value: cat.catEventId,
                })),
              };
            }}
            suffixIcon={<ArrowDownIcon />}
            allowClear
            size="large"
            placeholder={t(
              'claim.binderClaimsActions.lossDetails.selectCatCode'
            )}
            queryKey="cat-code-lazy-loading-select"
          />
        </Form.Item>
      </FormLayoutGroup>

      {/* Row 5: Cause of Loss, Cat Event */}
      <FormLayoutGroup mdColumns={2}>
        <Form.Item
          name="causeOfLoss"
          label={t('claim.binderClaimsActions.lossDetails.causeOfLoss')}
          rules={[{ required: true }]}
        >
          <SortedSelect
            suffixIcon={<ArrowDownIcon />}
            allowClear
            size="large"
            placeholder={t('claim.selectCauseOfLoss')}
            showSearch
            loading={isLoadingLossCauses}
            options={(lossCauses || []).map((lossCause) => ({
              label: lossCause.lossCauseName,
              value: lossCause.lossCauseName,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="catEvent"
          label={t('claim.binderClaimsActions.lossDetails.catEvent')}
        >
          <Input
            size="large"
            placeholder={t('general.enter', {
              field: t('claim.binderClaimsActions.lossDetails.catEvent'),
            })}
          />
        </Form.Item>
      </FormLayoutGroup>

      {/* Row 6: Loss Location, City, Country */}
      <FormLayoutGroup mdColumns={3}>
        <Form.Item
          name="lossLocation"
          label={t('claim.binderClaimsActions.lossDetails.lossLocation')}
        >
          <Input
            size="large"
            placeholder={t('claim.enterLossLocation')}
            autoComplete="off"
          />
        </Form.Item>

        <Form.Item
          name="city"
          label={t('claim.binderClaimsActions.lossDetails.city')}
          rules={[{ required: true }]}
        >
          <SortedSelect
            allowClear
            showSearch
            size="large"
            placeholder={t('general.selectField', {
              field: t('general.city'),
            })}
            loading={isLoadingCities}
            disabled={!countryIso}
            suffixIcon={<ArrowDownIcon />}
            className="w-full"
            options={uniqBy(cities || [], (city) => city.name).map((city) => ({
              value: city.name,
              label: city.name,
              key: city.id,
            }))}
            optionFilterProp="value"
          />
        </Form.Item>

        <Form.Item
          name="country"
          label={t('claim.binderClaimsActions.lossDetails.country')}
          rules={[{ required: true }]}
        >
          <SortedSelect
            allowClear
            showSearch
            size="large"
            loading={isLoadingCountries}
            placeholder={t('general.selectField', {
              field: t('general.country'),
            })}
            suffixIcon={<ArrowDownIcon />}
            className="w-full"
            options={uniqBy(countries || [], (country) => country.name).map(
              (country) => ({
                value: country.name,
                label: `${country.emoji} ${country.name}`,
                key: country.id,
              })
            )}
            optionFilterProp="value"
            onChange={handleCountryChange}
          />
        </Form.Item>
      </FormLayoutGroup>
    </FormSection>
  );
};

export default LossDetails;
